import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import {
  BarChart3,
  Brain,
  Code2,
  Lightbulb,
  Activity,
  Home,
  GitBranch,
  Settings
} from 'lucide-react';

// Import components
import HealthStatus from '@/components/HealthStatus';
import ProjectAnalysis from '@/components/ProjectAnalysis';
import AIRouting from '@/components/AIRouting';
import Suggestions from '@/components/Suggestions';
import SystemMetrics from '@/components/SystemMetrics';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function App() {
  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Health Status', href: '/health', icon: Activity },
    { name: 'Project Analysis', href: '/analyze', icon: Code2 },
    { name: 'AI Routing', href: '/ai-routing', icon: Brain },
    { name: 'Suggestions', href: '/suggestions', icon: Lightbulb },
    { name: 'Metrics', href: '/metrics', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Router>
        {/* Header */}
        <Header />

        {/* Main Content */}
        <main className="container mx-auto px-4 py-8">
          <Routes>
            <Route path="/" element={<HomeView navigation={navigation} />} />
            <Route path="/health" element={<HealthStatus />} />
            <Route path="/analyze" element={<ProjectAnalysis />} />
            <Route path="/ai-routing" element={<AIRouting />} />
            <Route path="/suggestions" element={<Suggestions />} />
            <Route path="/metrics" element={<SystemMetrics />} />
          </Routes>
        </main>

        {/* Footer */}
        <Footer />

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </Router>
    </div>
  );
}

// Home View Component
function HomeView({ navigation }: { navigation: any[] }) {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
          Universal Cross-Language
          <span className="text-primary-600"> Developer Assistant</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Seamlessly migrate and analyze projects across different programming ecosystems with AI-powered insights and cross-language compatibility.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Supported Languages"
          value="10+"
          description="JavaScript, Python, Java, Go, Rust, and more"
          icon={<Code2 className="w-6 h-6" />}
        />
        <StatCard
          title="AI Models"
          value="5+"
          description="Claude, GPT-4, Qwen, and specialized models"
          icon={<Brain className="w-6 h-6" />}
        />
        <StatCard
          title="API Endpoints"
          value="6"
          description="Health, Analysis, AI Routing, Suggestions, Metrics"
          icon={<GitBranch className="w-6 h-6" />}
        />
      </div>

      {/* Feature Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {navigation.slice(1).map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="group block p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-primary-300 transition-all duration-200"
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                <item.icon className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600">
                  {item.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {getFeatureDescription(item.name)}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* API Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">API Status</h2>
        <APIStatusCard />
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({
  title,
  value,
  description,
  icon
}: {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0 p-2 bg-primary-100 rounded-lg">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-2xl font-bold text-primary-600">{value}</p>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  );
}

// Feature Description Helper
function getFeatureDescription(name: string): string {
  switch (name) {
    case 'Health Status':
      return 'Monitor system health and service availability';
    case 'Project Analysis':
      return 'Analyze projects and detect languages and frameworks';
    case 'AI Routing':
      return 'Intelligent AI model selection for different tasks';
    case 'Suggestions':
      return 'Cross-language package and library suggestions';
    case 'Metrics':
      return 'System performance and usage metrics';
    default:
      return 'Explore powerful features';
  }
}

// API Status Card Component
function APIStatusCard() {
  const [status, setStatus] = React.useState<'checking' | 'online' | 'offline'>('checking');
  const [message, setMessage] = React.useState('Checking API availability...');

  React.useEffect(() => {
    const checkAPI = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.fastpm.dev'}/health`);
        if (response.ok) {
          setStatus('online');
          setMessage('API is online and operational');
        } else {
          setStatus('offline');
          setMessage('API is responding with errors');
        }
      } catch (error) {
        setStatus('offline');
        setMessage('Unable to connect to API - DNS propagation may be in progress');
      }
    };

    checkAPI();
    const interval = setInterval(checkAPI, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const statusColors = {
    checking: 'text-yellow-600 bg-yellow-100',
    online: 'text-green-600 bg-green-100',
    offline: 'text-red-600 bg-red-100',
  };

  const statusIcons = {
    checking: '⏳',
    online: '✅',
    offline: '❌',
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <span className="text-2xl">{statusIcons[status]}</span>
        <div>
          <p className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[status]}`}>
            {status.toUpperCase()}
          </p>
          <p className="text-sm text-gray-600 mt-1">{message}</p>
        </div>
      </div>
      <div className="text-sm text-gray-500">
        <p>API Endpoint: {import.meta.env.VITE_API_URL || 'https://api.fastpm.dev'}</p>
      </div>
    </div>
  );
}

export default App;